import SearchRounded from "@mui/icons-material/SearchRounded";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Proposal } from "../../../../types/proposal.types";
import ProposalCard from "./ProposalCard";
import { useState } from "react";

interface ProposalListProps {
  proposals: Proposal[];
  onCreateProposal?: () => void;
  onViewProposal?: (id: number) => void;
  loading?: boolean;
  totalCount?: number;
  nextUrl?: string | null;
  prevUrl?: string | null;
  onNext?: () => void;
  onPrev?: () => void;
}

export default function ProposalList({
  proposals,
  onCreateProposal,
  onViewProposal,
  loading = false,
  totalCount,
  nextUrl,
  prevUrl,
  onNext,
  onPrev,
}: ProposalListProps) {
  const [search, setSearch] = useState("");

  const filtered = proposals.filter((p) => {
    const title = (p.title ?? "").toLowerCase();
    const status = (p.status ?? "").toLowerCase();
    const contact =
      `${p.booking?.contact?.first_name ?? ""} ${p.booking?.contact?.last_name ?? ""}`.toLowerCase();
    const q = search.toLowerCase();

    return title.includes(q) || status.includes(q) || contact.includes(q);
  });

  return (
    <Paper sx={{ flex: 1, borderRadius: "14px", p: 2, boxShadow: 0 }}>
      <Stack
        direction="row"
        sx={{ mb: 2 }}
        alignItems="center"
        justifyContent="space-between"
      >
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search for Proposals"
          size="small"
          sx={{
            width: 360,
            maxWidth: "100%",
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              bgcolor: "#f8fafc",
              fontSize: "12px",
              height: 38,
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchRounded sx={{ color: "#94a3b8", fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          onClick={onCreateProposal}
          sx={{
            bgcolor: "#724e41",
            color: "white",
            borderRadius: "10px",
            px: 2.5,
            height: 38,
            textTransform: "none",
            fontWeight: 700,
            fontSize: "12px",
            whiteSpace: "nowrap",
            "&:hover": { bgcolor: "#5d3e33" },
          }}
        >
          Create New Proposal
        </Button>
      </Stack>

      {/* Cards  */}
      <Box
        sx={{
          maxHeight: 520,
          overflowY: "auto",
          pr: 0.5,
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "#e2e8f0",
            borderRadius: 4,
          },
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: "#724e41" }} />
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {filtered.map((proposal) => (
              <ProposalCard
                key={proposal.id}
                proposal={proposal}
                onView={onViewProposal}
              />
            ))}
          </Stack>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mt: 1,
          gap: 1,
        }}
      >
        <Typography variant="body2" sx={{ color: "#64748b", fontSize: "12px" }}>
          {totalCount} total
        </Typography>
        <IconButton disabled={!prevUrl} onClick={onPrev} size="small">
          <ChevronLeft />
        </IconButton>
        <IconButton disabled={!nextUrl} onClick={onNext} size="small">
          <ChevronRight />
        </IconButton>
      </Box>
    </Paper>
  );
}
